import { useContext } from 'react'

import { digObject, snakeToTitleCase, toggleArray } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Button, Checkbox, Form, ListItem, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { confirmAction } from '@functions/entity'

import useCampaign from '@hooks/useCampaign'

import CampaignSearchWithImage from '@components/CampaignSearchWithImage'
import SelectedCampaign from '@components/SelectedCampaign'

const MODAL_KEY = 'MergeCampaignsModal'

export type ModalCallbacks = {
  closeModal: () => void,
  mergeCampaign: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const entityKeys = [
  'campaign_contacts',
  'campaign_items',
  'campaign_proposals',
  'campaign_quotes',
  'digital_pages',
  'images',
  'invoices',
  'orders',
]

type DefaultState = {
  archive_source_campaign: boolean,
  destination_campaign_id: number | null,
  merge_entity_keys: string[],
}

const defaultState: DefaultState = {
  archive_source_campaign: true,
  destination_campaign_id: null,
  merge_entity_keys: [...entityKeys],
}

const MergeCampaignsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign: initCampaign } = modalPayload

  const campaignPayload = useCampaign(initCampaign)

  const {
    callbacks: { mergeCampaigns: mergeFn },
    updating,
  } = campaignPayload

  const [state, setState] = useSetState(defaultState)
  const { destination_campaign_id, merge_entity_keys } = state

  const confirmActionPayload = {
    callbacks: {
      actionFn: mergeFn,
      afterAction: closeModal,
    },
    options: {
      confirmButtonColor: '#43AC6A',
      entityParams: {
        ...state,
      },
      icon: 'warning',
      shouldRedirect: true,
    },
    strings: {
      actionString: 'merge',
      entityName: 'campaign',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Merge" titleSecondLine="Campaigns" />
      <SidebarModal.Content>
        <Form.Field label="From">
          <SelectedCampaign campaign={initCampaign} />
        </Form.Field>

        <Form.Field label="Copy to" marginTop="large">
          <CampaignSearchWithImage
            // @ts-expect-error TS2741
            callbacks={{
              selectItem: selectedCampaign => setState(
                { destination_campaign_id: selectedCampaign ? selectedCampaign.id : null },
              ),
            }}
          />
        </Form.Field>

        {!!destination_campaign_id
        && (
          <>
            <SidebarModal.Separator boxProps={{ marginY: 'large' }} />

            <Form.Field label="Merge Items">
              {entityKeys.map(entityKey => (
                // @ts-expect-error TS2339
                <ListItem.WithCheckbox
                  boxProps={{ border: 'none' }}
                  checkbox={(
                    <Checkbox
                      checked={merge_entity_keys?.includes(entityKey)}
                      onClick={() => setState({ merge_entity_keys: toggleArray(merge_entity_keys, entityKey) })}
                    />
              )}
                  key={entityKey}
                  onClick={() => setState({ merge_entity_keys: toggleArray(merge_entity_keys, entityKey) })}
                >

                  <Box color="bodyFontLightColor" fontSize="small">
                    {snakeToTitleCase(entityKey)}
                  </Box>

                  {/*
                 // @ts-expect-error TS2339 */}
                </ListItem.WithCheckbox>
              ))}
            </Form.Field>

            <Form.Field label="Archive Source Campaign" marginTop="medium">
              <select
                name="archive_source_campaign"
                onChange={e => setState({ archive_source_campaign: e.currentTarget.value === 'true' })}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </Form.Field>
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!destination_campaign_id || !merge_entity_keys?.length}
          loading={updating}
          loadingBubbleColor="white"
          // @ts-expect-error TS2345
          onClick={() => confirmAction(confirmActionPayload)}
          size="large"
        >
          Merge Campaigns
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <MergeCampaignsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
